import React, { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ReduxControls, Icon } from "rhombus-react";
import { Field, getFormValues, change } from "redux-form";
import PropTypes from "prop-types";
import classNames from "classnames";

import {
	TIKTOK_DISCLAIMER_TYPES,
	TIKTOK_STANDARD_DISCLAIMER_MAX_LENGTH,
	TIKTOK_CLICKABLE_TEXT_MAX_LENGTH,
	TIKTOK_MAX_CLICKABLE_OPTIONS
} from "social/constants/common";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { SOCIAL_AD_TYPES } from "utils/constants";

import "./TikTokDisclaimer.css";

const STANDARD_TEXT_PLACEHOLDER = "Enter disclaimer text for your TikTok creative";
const AD_TYPE = SOCIAL_AD_TYPES.TIKTOK_VIDEO;

let clickableOptionIdCounter = 0;
export function createClickableOption(overrides = {}) {
	clickableOptionIdCounter += 1;
	return { id: `clickable-${clickableOptionIdCounter}`, text: "", url: "", ...overrides };
}

const DEFAULT_CLICKABLE_OPTIONS = [createClickableOption()];

function useDisclaimerFormState() {
	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const adTypeValues = formValues[AD_TYPE] || {};
	const disclaimerType = adTypeValues.disclaimerType || TIKTOK_DISCLAIMER_TYPES.STANDARD;
	const clickableOptions = adTypeValues.clickableOptions || DEFAULT_CLICKABLE_OPTIONS;
	return { disclaimerType, clickableOptions };
}

function useClickableActions() {
	const dispatch = useDispatch();
	const { clickableOptions } = useDisclaimerFormState();
	const fieldPath = `${AD_TYPE}.clickableOptions`;

	const addOption = useCallback(() => {
		if (clickableOptions.length < TIKTOK_MAX_CLICKABLE_OPTIONS) {
			dispatch(change(SOCIAL_WIZARD, fieldPath, [...clickableOptions, createClickableOption()]));
		}
	}, [dispatch, clickableOptions, fieldPath]);

	const removeOption = useCallback((index) => {
		const updated = clickableOptions.filter((_option, idx) => idx !== index);
		dispatch(change(SOCIAL_WIZARD, fieldPath, updated));
	}, [dispatch, clickableOptions, fieldPath]);

	return { addOption, removeOption };
}

function StandardSection({ disabled, showPristineErrors }) {
	return (
		<Field
			name="disclaimerText"
			key="disclaimerText"
			label="Disclaimer Text"
			required
			className="tiktok-disclaimer-textarea"
			component={ReduxControls.Input}
			type="textarea"
			placeholder={STANDARD_TEXT_PLACEHOLDER}
			disabled={disabled}
			charMax={TIKTOK_STANDARD_DISCLAIMER_MAX_LENGTH}
			showCharCount
			showPristineErrors={!!showPristineErrors}
		/>
	);
}

function ClickableOptionRow({ index, canRemove, onRemove, disabled, showPristineErrors }) {
	return (
		<div className="tiktok-disclaimer-clickable-row">
			<div className="clickable-fields">
				<Field
					name={`clickableOptions[${index}].text`}
					label="Text"
					required
					component={ReduxControls.Input}
					charMax={TIKTOK_CLICKABLE_TEXT_MAX_LENGTH}
					showCharCount
					disabled={disabled}
					showPristineErrors={!!showPristineErrors}
				/>
				<Field
					name={`clickableOptions[${index}].url`}
					label="URL"
					required
					placeholder="https://"
					component={ReduxControls.Input}
					disabled={disabled}
					showPristineErrors={!!showPristineErrors}
				/>
			</div>
			{canRemove && (
				<button
					className="tiktok-disclaimer-remove-btn"
					onClick={() => onRemove(index)}
					type="button"
					disabled={disabled}
				>
					<Icon name="far fa-trash-can" />
				</button>
			)}
		</div>
	);
}

function ClickableSection({ disabled, showPristineErrors }) {
	const { clickableOptions } = useDisclaimerFormState();
	const { addOption, removeOption } = useClickableActions();
	const canAddMore = clickableOptions.length < TIKTOK_MAX_CLICKABLE_OPTIONS;

	return (
		<div className="tiktok-disclaimer-clickable-section">
			<h3>Add up to 3 clickable texts</h3>
			{clickableOptions.map((_option, index) => (
				<ClickableOptionRow
					key={_option.id}
					index={index}
					canRemove={clickableOptions.length > 1}
					onRemove={removeOption}
					disabled={disabled}
					showPristineErrors={showPristineErrors}
				/>
			))}
			{canAddMore && (
				<button
					className="tiktok-disclaimer-add-btn"
					onClick={addOption}
					type="button"
					disabled={disabled}
				>
					+ Add another clickable text
				</button>
			)}
		</div>
	);
}

export default function TikTokDisclaimer(props) {
	const {
		input: { value, onChange } = {},
		disabled = false,
		isCreativeViewOnly = false,
		hasExistingDisclaimer = false,
		className = "",
		showPristineErrors,
	} = props;

	const { disclaimerType } = useDisclaimerFormState();

	const toggleLocked = disabled || (isCreativeViewOnly && hasExistingDisclaimer);
	const typeLocked = disabled || (isCreativeViewOnly && hasExistingDisclaimer);
	const contentDisabled = disabled;

	const onToggleChange = useCallback((e) => {
		e.stopPropagation();
		if (toggleLocked) { return; }
		onChange(!value);
	}, [toggleLocked, onChange, value]);

	const isStandard = disclaimerType === TIKTOK_DISCLAIMER_TYPES.STANDARD;
	const isClickable = disclaimerType === TIKTOK_DISCLAIMER_TYPES.CLICKABLE;

	return (
		<div
			className={classNames("tiktok-disclaimer-wrapper", "e-form__control-wrapper", {
				[className]: !!className,
			})}
		>
			<div className="full-width e-form__control-header">
				<div
					className={classNames("tiktok-disclaimer-field", {
						"is-checked": !!value,
						"is-locked": toggleLocked,
					})}
					onClick={onToggleChange}
				>
					<div className="tiktok-disclaimer-slider"> </div>
				</div>
				<div className="e-form__control-label">
					Disclaimer&nbsp;
					<Icon name="help-tip fas fa-info-circle" data-tip="Adding disclaimers to your ads could help you avoid legal risks." />
				</div>
			</div>
			{!!value && (
				<div className="full-width">
					<div className="tiktok-disclaimer-body">
						<div className="tiktok-disclaimer-type-row">
							<span className="tiktok-disclaimer-type-label">Disclaimer Type</span>
							<div className="options">
								<Field
									name="disclaimerType"
									type="radio"
									value={TIKTOK_DISCLAIMER_TYPES.STANDARD}
									label="Standard"
									component={ReduxControls.Checkbox}
									disabled={typeLocked}
									showPristineErrors={!!showPristineErrors}
								/>
								<Field
									name="disclaimerType"
									type="radio"
									value={TIKTOK_DISCLAIMER_TYPES.CLICKABLE}
									label="Clickable"
									component={ReduxControls.Checkbox}
									disabled={typeLocked}
									showPristineErrors={!!showPristineErrors}
								/>
							</div>
						</div>

						{isStandard && (
							<StandardSection disabled={contentDisabled} showPristineErrors={showPristineErrors} />
						)}

						{isClickable && (
							<ClickableSection disabled={contentDisabled} showPristineErrors={showPristineErrors} />
						)}
					</div>
				</div>
			)}
		</div>
	);
}

TikTokDisclaimer.displayName = "TikTokDisclaimer";

TikTokDisclaimer.propTypes = {
	input: PropTypes.shape({
		value: PropTypes.any,
		onChange: PropTypes.func,
	}),
	disabled: PropTypes.bool,
	isCreativeViewOnly: PropTypes.bool,
	hasExistingDisclaimer: PropTypes.bool,
	className: PropTypes.string,
	showPristineErrors: PropTypes.bool,
};
